import { TranslationsContext } from '@teliaee/telia-translate';
import { inject } from 'mobx-react';
import * as React from 'react';
import {
  AnalyticsTrigger,
  Banner,
  BannerHeading,
  BannerItem,
  Button,
  EqualHeight,
  Modal,
  ModalProvider,
  ModalTrigger,
  Separator,
  Text,
  THeadingLevel
} from 'telia-front-react';
import JsxRenderer from '../content/JsxRenderer';
import RouteStore from '../stores/RouteStore';
import { getPersonalOfferImageProps } from '../util/ImageUtil';
import DiscountCodeOffer from './DiscountCodeOffer';
import { Offer, OfferActions, OfferType } from './OfferInterfaces';
import OfferViewAsAnalyticsHook from './OfferViewAnalyticsHook';

interface PersonalOffersCardProps {
  headingLevel: THeadingLevel;
  offer: Offer;
  ctaLabel: string;
  offerActions: OfferActions;
  routeStore?: RouteStore;
}

const PersonalOffersCard = (props: PersonalOffersCardProps): JSX.Element => {
  const { translate } = React.useContext(TranslationsContext);
  const { headingLevel, offer, ctaLabel, offerActions, routeStore } = props;
  offerActions.present(offer);

  const renderCTA = (inModal: boolean) => {
    const button = (
      <Button
        text={offer.ctaLabel || ctaLabel || translate('offer.acceptButtonLabel')}
        iconRight="#arrow-right"
        url={offer.url}
        onClick={(event) => {
          event.preventDefault();
          offerActions.accept(offer.recommendationItemId);
          if (!inModal) {
            routeStore.push(offer.url);
          }
        }}
      />
    );
    return (
      <AnalyticsTrigger trigger={{ id: `Link - ${offer.slug}`, callback: 'onClick' }}>
        {inModal ? <ModalTrigger>{button}</ModalTrigger> : button}
      </AnalyticsTrigger>
    );
  };

  const renderCardContent = (inModal: boolean) => {
    return (
      <>
        <OfferViewAsAnalyticsHook offer={offer} isShopOffer={false} />
        <Banner background="brand-primary-bg-subtle">
          <BannerItem id={`banner-item-${offer.id}`} image={getPersonalOfferImageProps(offer.image, offer.mobileImage)}>
            <EqualHeight group="offer-content">
              {offer.title && (
                <BannerHeading level={headingLevel} className="h3">
                  {offer.title}
                </BannerHeading>
              )}
              <Text>
                <JsxRenderer jsx={offer.excerpt} showErrorNotice={false} doNotScroll={true} />
              </Text>
            </EqualHeight>
            <Separator spacing="small" color="invisible" />
            <EqualHeight group="offer-cta">{renderCTA(inModal)}</EqualHeight>
          </BannerItem>
        </Banner>
      </>
    );
  };

  if (offer.offerType === OfferType.DISCOUNT) {
    return <DiscountCodeOffer offer={offer} headingLevel={headingLevel} offerActions={offerActions} />;
  }
  if (offer.modal) {
    return (
      <ModalProvider>
        {renderCardContent(true)}
        <Modal size="8-col">
          <JsxRenderer jsx={offer.content} showErrorNotice={false} renderInWrapper={false} doNotScroll={true} />
        </Modal>
      </ModalProvider>
    );
  }

  return renderCardContent(false);
};

export default inject('routeStore')(PersonalOffersCard);
